import tenantUtils from '@utils';
import tenantTransformers from '@transformers';
import tenantPayloads from '@payloads';
import tenantData from '@data';
import store from '@store';
import { getEndPointArgs } from '../../../hooks/useRtkCacheUpdate';
import { convertQueryObjToString } from '../../../utility/urlQuery';
import { normalizeSurgeListingItem } from '../../../utility/utility';
import { isUnifiedProfolioEnabled } from '../../../utility/user';
import listingsApis from '../../../apis/listings';
import { formatListingDetailResponse } from '../../common/transformers/drawerListingDetail';

const normalizeOmanApplyProductListing = (listing) => {
  if (!listing?.platform_listings?.length) {
    return normalizeSurgeListingItem(listing);
  }
  return normalizeSurgeListingItem({
    ...listing,
    platform_listings: listing.platform_listings.map((pl) => {
      const products = pl?.products_information;
      if (!products || Array.isArray(products)) {
        return pl;
      }
      return {
        ...pl,
        products_information:
          pl?.products_information_formatted ||
          (typeof products === 'object' ? Object.values(products) : []),
      };
    }),
  });
};

const listingsApiEndpoints = {
  getTruCheckStatuses: () => {
    return {
      url: `/api/surge/statuses?search_class=trucheck`,
    };
  },

  getMyListings: () => {
    return {
      url: `/api/surge/statuses?search_class=trucheck`,
    };
  },
  getListingsStats: {
    queryFn: async ({ listingIds, userIds, userId, surgeListingIds }, { getState, dispatch }, extraOptions, baseQuery) => {
      const loginUser = store.getState().app.loginUser?.user;
      const isAgency = loginUser?.agency?.id;
      let user = {};
      if (isAgency) {
        user = loginUser?.agency?.users?.find((e) => e?.id == userId);
      } else {
        user = loginUser;
      }
      if (!user || userId == -1) {
        user = {
          ...loginUser,
          ...loginUser?.agency,
          name: loginUser?.agency?.name,
          name_l1: loginUser?.agency?.name_l1,
          id: -1,
          profile_image: loginUser?.agency?.agency_logo,
          is_agency_admin: true,
        };
      }
      const isUnifiedListings = isUnifiedProfolioEnabled({ featureKey: 'listingsUnified' });
      try {
        if (isUnifiedListings && surgeListingIds?.length) {
          try {
            const uniqueIds = [...new Set(surgeListingIds.filter((id) => id != null && id !== ''))];
            if (uniqueIds.length) {
              const qs = convertQueryObjToString({ listing_ids: uniqueIds });
              const surgeResult = await baseQuery(`api/surge/bi/dashboard/listing_stats?${qs}`);
              if (surgeResult?.error) {
                console.error('Unified listing stats (surge) error:', surgeResult.error);
              } else {
                const surgePayload = surgeResult?.data ?? surgeResult;
                const statsById = surgePayload?.stats ?? {};
                const listingQueryArgs = getEndPointArgs('getMyListings', getState().parentApi);

                dispatch(
                  listingsApis.util.updateQueryData('getMyListings', listingQueryArgs, (draftListings) => {
                    draftListings.list?.forEach((listing) => {
                      if (!listing?.platforms?.unified) return;
                      const row = statsById[String(listing.id)] ?? statsById[listing.id];
                      const patch = tenantTransformers.mapUnifiedSurgeBiListingStats(row);
                      listing.platforms.unified = {
                        ...listing.platforms.unified,
                        ...patch,
                      };
                    });
                  }),
                );
              }
            }
          } catch (e) {
            console.error('Failed unified listing stats (surge):', e);
          }
        }

        await Promise.all(
          user?.platforms.map(async (platform) => {
            const payload = tenantPayloads.getListingStatsPayload({
              listingIDs: listingIds?.[platform?.slug],
              userIDs: userIds?.[platform?.slug],
              platformId: platform?.id,
            });

            const result = await baseQuery(`/api/surge/ovation/stats?${payload}`);
            const listingQueryArgs = getEndPointArgs('getMyListings', getState().parentApi);

            const { data } = result;

            dispatch(
              listingsApis.util.updateQueryData('getMyListings', listingQueryArgs, (draftListings) => {
                draftListings.list?.forEach((listing) => {
                  const findListing = data?.stats?.items?.find(
                    (it) =>
                      it?.ad_external_id == listing?.platforms?.[platform?.slug]?.platform_listing_id ||
                      it?.ad_external_id == listing?.id,
                  );
                  const statsPatch = tenantTransformers.updateListingStats(findListing);
                  const platformListing = listing?.platforms?.[platform?.slug];
                  if (platformListing) {
                    listing.platforms[platform?.slug] = {
                      ...platformListing,
                      ...statsPatch,
                    };
                  }
                });
              }),
            );
          }),
        );

        const listingQueryArgs = getEndPointArgs('getMyListings', getState().parentApi);
        dispatch(
          listingsApis.util.updateQueryData('getMyListings', listingQueryArgs, (draftListings) => {
            tenantTransformers.finalizeUnifiedListingStatsPlaceholders(draftListings);
          }),
        );

        return { data: 'Success' };
      } catch (error) {
        console.error('Failed to fetch listing stats:', error);
        return { error: { status: 'FETCH_FAILED', error: error.message } };
      }
    },
  },

  listingDeleteReasons: {
    query: (purposeId) => `/api/surge/reasons?q[listing_category_id_eq]=${purposeId}&q[reason_type_eq]=deletion`,
  },

  deleteListing: {
    query: (body) => {
      return {
        url: `/api/surge/listings/${body?.listingId}`,
        method: 'DELETE',
        body: body,
      };
    },
    afterSuccess: async ({ listingId }, { dispatch, queryFulfilled, getState }) => {
      const user = getState().app?.loginUser?.user;
      const listingQueryArgs = getEndPointArgs('getMyListings', getState().parentApi);
      try {
        const { data } = await queryFulfilled;

        dispatch(
          listingsApis.util.updateQueryData('getMyListings', listingQueryArgs, (draftListings) => {
            if (draftListings?.list) {
              draftListings.list = draftListings.list.map((listing) =>
                listing.id === listingId
                  ? tenantTransformers.listingMapper(data?.listing, user, false, listing)
                  : listing,
              );
            }
          }),
        );
      } catch {
        console.error('Failed to delete the listing.');
      }
    },
  },

  deleteListingFromDubizzle: {
    query: (body) => {
      return {
        url: `/api/surge/listings/${body?.listingId}/destroy_platform_listing`,
        method: 'POST',
        body: { platform_slug: 'dubizzle' },
      };
    },
    afterSuccess: async ({ listingId }, { dispatch, queryFulfilled, getState }) => {
      const listingQueryArgs = getEndPointArgs('getMyListings', getState().parentApi);
      try {
        await queryFulfilled;

        dispatch(
          listingsApis.util.updateQueryData('getMyListings', listingQueryArgs, (draftListings) => {
            if (draftListings?.list) {
              draftListings.list = draftListings.list.filter((listing) => listing.id !== listingId);
            }
          }),
        );
      } catch {
        console.error('Failed to delete the listing.');
      }
    },
  },

  fetchListingDetail: {
    query: ({ userId, listingId }) => {
      return {
        url: `/api/surge/users/${userId}/listings/${listingId}?include=active_images`,
      };
    },
    transformer: (response) => {
      const active_images = response?.data?.data?.active_images;
      const listingImages = active_images.map((e) => ({
        ...e,
        ...imageStateObject('select'),
      }));
      return { active_images: listingImages };
    },
  },

  getApplicableProducts: (body) => {
    return {
      url: `/api/surge/products/applicable_products?${body?.requestParams ? convertQueryObjToString(body?.requestParams) : ''}`,
      transformer: (res, meta, { platformProductSlugs }) => {
        const normalized = tenantTransformers.normalizeApplicableProductsResponse(res) || {};
        const platforms = tenantData.platformList;
        let data = {};

        platforms?.forEach((e) => {
          const platformData = normalized[e?.slug];
          if (platformData) {
            data[e?.slug] = {
              ...platformData,
              applicableProduct: tenantTransformers.getProductDetailToBeApplied(
                platformData,
                platformProductSlugs?.[e?.slug],
              ),
              products: tenantTransformers.getProductDetails(platformData?.products),
            };
          }
        });

        if (normalized?.unified) {
          const unifiedProductSlug =
            platformProductSlugs?.unified ?? platformProductSlugs?.bayut ?? 'basic-listing';
          data.unified = {
            ...normalized.unified,
            applicableProduct: tenantTransformers.getProductDetailToBeApplied(
              normalized.unified,
              unifiedProductSlug,
            ),
            products: tenantTransformers.getProductDetails(normalized?.unified?.products),
          };
        }

        return data;
      },
    };
  },

  getUpsellDetail: {
    queryFn: async (params, api, extraOptions, baseQuery) => {
      const requestParams = {
        listing_id: params?.listing_id,
        ...(params?.platformProductSlugs &&
          typeof params.platformProductSlugs === 'object' && {
            platformProductSlugs: JSON.stringify(params.platformProductSlugs),
          }),
      };
      const queryString = convertQueryObjToString(requestParams);

      const apiCalls = [
        baseQuery({ url: `/api/surge/listings/${params?.listing_id}/edit` }),
        baseQuery({ url: `/api/surge/products/applicable_products?${queryString}` }),
      ];

      const results = await Promise.allSettled(apiCalls);

      const rawListingResponse = results?.[0]?.value?.data;
      const formattedListingResponse = rawListingResponse?.listing
        ? formatListingDetailResponse(rawListingResponse)
        : rawListingResponse;
      const listing = formattedListingResponse?.listing ?? rawListingResponse?.listing;

      const listingDetails = {
        ...rawListingResponse,
        listing,
        isListingPosted: listing?.is_posted ?? rawListingResponse?.listing?.is_posted,
        listingExpiryDays: listing?.expiry_days ?? rawListingResponse?.listing?.expiry_days,
      };

      const applicableRaw = results?.[1]?.value?.data;
      const normalizedApplicable = tenantTransformers.normalizeApplicableProductsResponse?.(applicableRaw) ?? applicableRaw;

      return {
        data: tenantTransformers.upsellDataMapper(
          normalizedApplicable,
          listingDetails,
          params?.platformProductSlugs,
        ),
      };
    },
  },

  applyProduct: (query) => {
    return {
      url: `/api/surge/listings/${query?.listingId}/apply_products`,
      method: 'PUT',
      body: query?.body,
      afterSuccess: async ({ listingId }, { dispatch, queryFulfilled, getState }) => {
        const listingQueryArgs = getEndPointArgs('getMyListings', getState().parentApi);
        const user = getState().app?.loginUser?.user;
        try {
          const { data } = await queryFulfilled;
          const listing = normalizeOmanApplyProductListing(data?.listing);
          if (!!listing) {
            dispatch(
              listingsApis.util.updateQueryData('getMyListings', listingQueryArgs, (draftListings) => {
                draftListings?.list?.forEach((e, i) => {
                  if (e?.id == listingId) {
                    let updateListing = { ...draftListings.list[i] };
                    tenantData.platformList.forEach((platform) => {
                      if (updateListing?.platforms?.[platform?.slug]) {
                        updateListing.platforms[platform?.slug] = {
                          ...updateListing.platforms[platform?.slug],
                          ...tenantTransformers.platformMapper(listing, user, platform, false),
                        };
                      }
                    });
                    if (isUnifiedProfolioEnabled({ featureKey: 'listingsUnified' }) && updateListing?.platforms?.unified) {
                      updateListing.platforms.unified = {
                        ...updateListing.platforms.unified,
                        ...tenantTransformers.platformMapper(listing, user, { slug: 'unified', icon: 'BayutSmallLogo' }, false),
                        stats: { views: 0, clicks: 0, leads: 0 },
                        views: 0,
                        clicks: 0,
                        leads: 0,
                      };
                    }
                    (updateListing.platforms.listingRowActions = (refsObject, loading) => {
                      const actions = tenantUtils.getListingRowActions(listing, refsObject, loading, user);
                      return actions;
                    }),
                      (draftListings.list[i] = updateListing);
                  }
                });
              }),
            );
          }
        } catch {
          console.error('Failed to apply upgrade.');
        }
      },
    };
  },

  updateAutoRenew: {
    query: ({ autoRenewId, listingId, productId, autoRenewableId, autoRenewableType }) => {
      const resolvedAutoRenewableId = autoRenewableId || listingId;
      const resolvedAutoRenewableType = autoRenewableType || 'Listing';
      return {
        url: autoRenewId ? `/api/surge/auto_renewable_items/${autoRenewId}` : '/api/surge/auto_renewable_items',
        method: autoRenewId ? 'DELETE' : 'POST',
        body: !autoRenewId
          ? {
              auto_renewable_item: {
                auto_renewable_id: resolvedAutoRenewableId,
                auto_renewable_type: resolvedAutoRenewableType,
                product_id: productId,
              },
            }
          : {},
      };
    },
    transformer: (response, meta, arg = {}) => {
      const { listingId, autoRenewableId, autoRenewId } = arg;
      const resolvedAutoRenewableId = autoRenewableId || listingId;
      const base = {
        listing_id: resolvedAutoRenewableId,
        replaceKey: 'platforms.auto_renewable_item',
        valueExists: true,
      };
      const item = response?.data?.auto_renewable_item ?? response?.auto_renewable_item;
      if (item && typeof item === 'object') {
        return {
          ...item,
          isApplied: !!(item.auto_renewable_id ?? item.id),
          id: item.id ?? null,
          renewing_on: item.renewing_on ?? null,
          ...base,
        };
      }
      if (autoRenewId) {
        return { isApplied: false, id: null, renewing_on: null, ...base };
      }
      if (response != null && response !== '') {
        return { isApplied: true, id: null, renewing_on: null, ...base };
      }
      return undefined;
    },
  },

  transferListings: {
    query: ({ userId, listingIds }) => {
      return {
        url: '/api/surge/listings/transfer_listings',
        method: 'PUT',
        body: {
          listing: {
            user_id: userId,
            listing_ids: listingIds,
          },
        },
      };
    },
  },

  postPackageOnListing: {
    query: (params) => {
      const endpoints = {
        shot_listing: {
          url: `/api/surge/listings/${listingId}/mark_superhot`,
          body: { ...data, subject_id: data?.user_id },
          method: 'PATCH',
        },
        hot_listing: {
          url: `/api/surge/listings/${listingId}/mark_hot`,
          body: { ...data, subject_id: data?.user_id },
          method: 'PATCH',
        },
        refresh_listing: {
          url: `/api/surge/listings/${listingId}/refresh`,
          body: { ...data, subject_id: data?.user_id },
          method: 'PATCH',
        },
        story_ad: {
          url: `/api/surge/stories`,
          body: { ...data },
          method: 'POST',
        },
        olx_refresh_listing: {
          url: `/api/surge/listings/${listingId}/apply_olx_credits`,
          body: { product_type: 'olx_refresh_listing', ...data },
          method: 'POST',
        },
        olx_feature: {
          url: `/api/surge/listings/${listingId}/apply_olx_credits`,
          body: { product_type: 'olx_feature', ...data },
          method: 'POST',
        },
        property_videography: {
          url: `/api/surge/listings/${listingId}/verified_property_media`,
          body: { ...data, subject_id: data?.user_id },
          method: 'PATCH',
        },
      };
      const { listingId, action, data = {} } = params;

      return endpoints?.[action?.slug];
    },
    afterSuccess: async ({ listingId }, { dispatch, queryFulfilled, getState }) => {
      const listingQueryArgs = getEndPointArgs('getMyListings', getState().parentApi);
      const user = getState().app?.loginUser?.user;
      try {
        const { data } = await queryFulfilled;
        if (!!data?.listing) {
          dispatch(
            listingsApis.util.updateQueryData('getMyListings', listingQueryArgs, (draftListings) => {
              draftListings?.list?.forEach((e, i) => {
                if (e?.id == listingId) {
                  draftListings.list[i] = {
                    ...draftListings?.list?.[i],
                    ...tenantTransformers.listingMapper(data?.listing, user, true),
                  };
                }
              });
            }),
          );
        } else {
          return { message: 'Credit applied Successfully!' };
        }
      } catch {
        console.error('Failed to apply upgrade.');
      }
    },
  },

  getListingCardDetail: {
    query: ({ listingId }) => {
      return `/api/surge/listings/${listingId}/edit`;
    },
    transformer: (res) => {
      return tenantTransformers.listingCardMapper(res);
    },
  },
};

export default listingsApiEndpoints;
